/**
 * Share link API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type {
  CVNote,
  CVNoteListResponse,
  PublicCVDetail,
  PublicNoteCreateInput,
  PublicShareData,
  ReorderRequest,
  ReviewStatus,
  ShareLinkCreateInput,
  ShareLinkListResponse,
  ShareLinkResponse,
} from '../types';

// API base URL for public endpoints (must include /api/v1)
const ATS_API_BASE = import.meta.env.VITE_ATS_API_URL || 'http://localhost:8200';
const API_BASE_URL = `${ATS_API_BASE}/api/v1`;

interface ShareLinkApiResponse {
  success: boolean;
  data: ShareLinkResponse;
  message: string;
}

interface ShareLinkListApiResponse {
  success: boolean;
  data: ShareLinkListResponse;
  message: string;
}

interface PublicShareApiResponse {
  success: boolean;
  data: PublicShareData;
  message: string;
}

interface PublicNoteApiResponse {
  success: boolean;
  data: CVNote;
  message: string;
}

interface PublicNoteListApiResponse {
  success: boolean;
  data: CVNoteListResponse;
  message: string;
}

interface PublicCVDetailApiResponse {
  success: boolean;
  data: PublicCVDetail;
  message: string;
}

class ShareService {
  // ==================== Authenticated endpoints ====================

  /**
   * Create a share link for selected CVs.
   */
  async createShareLink(
    jobId: string,
    workspaceId: string,
    idToken: string,
    data: ShareLinkCreateInput
  ): Promise<ShareLinkResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<ShareLinkApiResponse>(`/jobs/${jobId}/share`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create share link');
    }

    return response.data.data;
  }

  /**
   * List share links for a job.
   */
  async listShareLinks(
    jobId: string,
    workspaceId: string,
    idToken: string,
    includeExpired = false
  ): Promise<ShareLinkListResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<ShareLinkListApiResponse>(`/jobs/${jobId}/share-links`, {
      params: { include_expired: includeExpired },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list share links');
    }

    return response.data.data;
  }

  /**
   * Deactivate a share link.
   */
  async deactivateShareLink(
    jobId: string,
    shareLinkId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.delete<{ success: boolean; message: string }>(
      `/jobs/${jobId}/share-links/${shareLinkId}`,
      {
        headers: { 'X-Workspace-ID': workspaceId },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to deactivate share link');
    }
  }

  // ==================== Public endpoints (no auth required) ====================

  /**
   * Get shared CVs by token (public).
   */
  async getSharedCVs(token: string): Promise<PublicShareData> {
    const response = await fetch(`${API_BASE_URL}/public/share/${token}`);
    const data: PublicShareApiResponse = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Share link not found or expired');
    }

    return data.data;
  }

  /**
   * Get shared CV detail by token and CV ID (public).
   */
  async getSharedCVDetail(token: string, cvId: string): Promise<PublicCVDetail> {
    const response = await fetch(`${API_BASE_URL}/public/share/${token}/cvs/${cvId}`);
    const data: PublicCVDetailApiResponse = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'CV not found or not in share link');
    }

    return data.data;
  }

  /**
   * List notes for a shared CV (public).
   */
  async listPublicNotes(token: string, cvId: string): Promise<CVNoteListResponse> {
    const response = await fetch(`${API_BASE_URL}/public/share/${token}/cvs/${cvId}/notes`);
    const data: PublicNoteListApiResponse = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to list notes');
    }

    return data.data;
  }

  /**
   * Create a note on a shared CV (public, with optional auth).
   */
  async createPublicNote(
    token: string,
    cvId: string,
    noteData: PublicNoteCreateInput,
    idToken?: string
  ): Promise<CVNote> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // If user is logged in, include auth header to link note to their account
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/public/share/${token}/cvs/${cvId}/notes`, {
      method: 'POST',
      headers,
      body: JSON.stringify(noteData),
    });

    const data: PublicNoteApiResponse = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create note');
    }

    return data.data;
  }

  /**
   * Update review status for a CV in a share link (public).
   */
  async updateReviewStatus(
    token: string,
    cvId: string,
    reviewStatus: ReviewStatus
  ): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/public/share/${token}/cvs/${cvId}/review`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ review_status: reviewStatus }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to update review status');
    }
  }

  /**
   * Reorder CVs in a share link (public).
   */
  async reorderCVs(token: string, cvIds: string[]): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/public/share/${token}/reorder`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cv_ids: cvIds } satisfies ReorderRequest),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to reorder CVs');
    }
  }
}

const shareService = new ShareService();
export default shareService;
